import { css } from "@emotion/react";
import transitions from "./transitions";
import { fonts } from "./fonts";
import colours from "./colours";
import transform from "theme/transform";
import borderAndShadow from "theme/borderAndShadow";

const bloopTransitionProperty = (transitionTime?: string) => css`
    transition: transform ${transitionTime || transitions.transitionFaster};
    will-change: transform;
`;

const bloopScaleTransitionProperty = css`
    transition: scale ${transitions.transitionFaster};
    will-change: scale;
`;

const forceScrollbar = css`
    ::-webkit-scrollbar {
        -webkit-appearance: none;
        width: 7px;
    }

    ::-webkit-scrollbar-thumb {
        border-radius: 4px;
        background-color: ${colours.gold};
        -webkit-box-shadow: 0 0 1px ${colours.gold};
    }
`;

export const button = {
    reset: () => css`
        margin: 0;
        border: 0;
        padding: 0;
        background: none;

        :focus {
            outline: none;
        }
    `,
    /**
     * For when we have an anchor (link) that is to look/feel like a button.
     */
    buttonyAnchor: css`
        display: inline-flex;
        justify-content: center;
        align-items: center;
        background-color: buttonface;
        --anchor-button-text-colour: buttontext;
        color: var(--anchor-button-text-colour);

        // Specific hover colour for anchors is needed to override styles from our main stylesheet :'(
        :hover {
            color: var(--anchor-button-text-colour);
        }
    `,
    padded: css`
        padding: 0 1em;
    `,
    rounded: css`
        border-radius: ${borderAndShadow.roundCap};
    `,
    circular: css`
        border-radius: 50%;
    `,
    bordered: css`
        border-width: 1px;
        border-style: solid;
    `,
    underlined: css`
        text-decoration-line: underline;
        text-underline-offset: 0.5ex;
    `,
    backgroundColourable: css`
        --button-bg-colour: buttonface;
        background-color: var(--button-bg-colour);
        transition: background-color ${transitions.transitionFaster};

        :enabled:hover, :any-link:hover {
            background-color: color-mix(in srgb, var(--button-bg-colour), black 10%);
        }
    `,
    svgColourable: css`
        --button-svg-colour: currentcolor;
        transition: background-color ${transitions.transitionFaster};

        svg {
            fill: var(--button-svg-colour);
        }

        :enabled:hover svg, :any-link:hover svg {
            fill: color-mix(in srgb, var(--button-svg-colour), black 10%);
        }
    `,
    disabledGhost: css`
        :disabled, a&:not(:any-link) {
            transition: opacity 0s linear 0s;
            opacity: 0.5;
        }
    `,
    types: {
        nice: {
            get base() {
                return [
                    button.reset(),
                    button.padded,
                    button.rounded,
                    button.backgroundColourable,
                    button.disabledGhost,
                ];
            },
            get outlined() {
                return [
                    this.base,
                    button.bordered,
                    css`
                        --button-bg-colour: transparent;
                        border-color: currentcolor;
                    `,
                ];
            },
            get simpleOutlinedBlue() {
                return [
                    this.base,
                    css`
                        --button-bg-colour: ${colours.curiousBlue2};
                        
                        font-size: 22px;
                        font-weight: ${fonts.weights.bold};
                        cursor: pointer;
                        box-shadow: ${borderAndShadow.boxShadow};
                        color: white;
                        border: 1px solid white;
                    `,
                ]
            }
        },
        close: {
            dimensions: css`
                width: 2rem;
                height: 2rem;

                svg {
                    padding: 0.5rem;
                }
            `,
            colours: css`
                --button-bg-colour: ${colours.naturalGrey};
                --button-svg-colour: white;
            `,
            anchoredTopRight: css`
                position: absolute;
                top: 0;
                right: 0;
                transform: translate(40%, -40%);
            `,
            get base() {
                return [
                    button.reset(),
                    button.circular,
                    button.backgroundColourable,
                    button.svgColourable,
                    this.dimensions,
                    this.colours,
                ]
            },
        },
    }
};

const mixins = {
    transformedBloop: (extraTransform?: string, transitionTime?: string, transformFirst?: boolean) => css`
        ${bloopTransitionProperty(transitionTime)};

        &:hover {
            transform: ${transformFirst ? `${extraTransform} ${transform.bloopScale}` : `${transform.bloopScale} ${extraTransform}`};
        }
    `,
    bloop: css`
        ${bloopTransitionProperty()}
        &:hover {
            transform: ${transform.bloopScale};
        }
    `,
    bloopScale: `
        transform: ${transform.bloopScale};
    `,
    button,
    placeholderAttributes: (content?: any) => `
        font-family: ${fonts.frutiger};
        font-weight: ${fonts.weights.light};
        color: ${colours.grey[450]};
        opacity: 1; /** Safari / Firefox **/

        ${content};
    `,

    placeholder: (content?: any) => `
        &::placeholder { /** Chrome **/
            ${mixins.placeholderAttributes(content)};

            /** We put @content after the above include, so that the @content can override **/
            ${content};
        }

        /** This css doesn't work with the list below **/
        &::-webkit-input-placeholder, /** Opera/Safari **/
        &::-moz-placeholder, /** Firefox 19+ **/
        &:-moz-placeholder { /** Firefox 18- **/
            ${mixins.placeholderAttributes(content)};

            /** We put @content after the above include, so that the @content can override **/
            ${content};
        }

        /** IE: This css doesn't work with the list above **/
        &:-ms-input-placeholder {
            ${mixins.placeholderAttributes(content)};

            /** We put @content after the above include, so that the @content can override **/
            ${content};
        }

        /** Edge: This css doesn't work with the list above **/
        &::-ms-input-placeholder {
            ${mixins.placeholderAttributes(content)};

            /** We put @content after the above include, so that the @content can override **/
            ${content};
        }
    `,
    bloopScaleTransitionProperty,
    forceScrollbar,
};

export default mixins;